import { getDB } from './db';
import { apiClient } from '../api/client';

let syncTimeout = null;

async function notifySyncStatus(status) {
    const event = new CustomEvent('syncStatusChange', { detail: status });
    window.dispatchEvent(event);
}

export const habitStore = {
    async getAllHabits() {
        const db = await getDB();
        return db.getAll('habits');
    },

    async addHabit(habit) {
        const db = await getDB();
        const newHabit = {
            id: crypto.randomUUID(),
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            type: habit.type || 'binary',
            goal: habit.goal || 1,
            unit: habit.unit || null,
            direction: habit.direction || 'increase',
            repeat_days: habit.repeat_days || null,
            start_date: habit.start_date || null,
            end_date: habit.end_date || null,
            integration_id: habit.integration_id || null, // e.g., 'steps', 'distance'
            source_type: habit.source_type || null,       // e.g., 'apple_health', 'google_fit'
            is_deleted: 0,
            isDirty: true,
            title: habit.title
        };
        await db.add('habits', newHabit);
        this.scheduleSync();
        return newHabit;
    },

    async updateHabit(habitId, updates) {
        const db = await getDB();
        const habit = await db.get('habits', habitId);
        if (habit) {
            const updatedHabit = {
                ...habit,
                ...updates,
                updated_at: new Date().toISOString(),
                isDirty: true
            };
            await db.put('habits', updatedHabit);
            this.scheduleSync();
            return updatedHabit;
        }
    },

    async deleteHabit(habitId) {
        const db = await getDB();
        const habit = await db.get('habits', habitId);
        if (habit) {
            habit.is_deleted = 1;
            habit.isDirty = true;
            habit.updated_at = new Date().toISOString();
            await db.put('habits', habit);
            this.scheduleSync();
        }
    },

    async updateLog(habitId, date, value, isCompleted) {
        const db = await getDB();
        const tx = db.transaction('logs', 'readwrite');
        const index = tx.store.index('by-habit-date');
        const existing = await index.get([habitId, date]);

        const updatedAt = new Date().toISOString();

        if (existing) {
            await tx.store.put({
                ...existing,
                value,
                is_completed: isCompleted,
                updated_at: updatedAt,
                isDirty: true
            });
        } else {
            await tx.store.add({
                habitId,
                date,
                value,
                is_completed: isCompleted,
                updated_at: updatedAt,
                isDirty: true
            });
        }
        await tx.done;
        this.scheduleSync();
    },

    scheduleSync() {
        if (syncTimeout) clearTimeout(syncTimeout);
        syncTimeout = setTimeout(() => this.syncWithRemote(), 5000);
    },

    async syncWithRemote() {
        if (!navigator.onLine) {
            notifySyncStatus('offline');
            return;
        }

        notifySyncStatus('syncing');

        const db = await getDB();
        const habits = await db.getAll('habits');
        const logs = await db.getAll('logs');
        const dirtyHabits = habits.filter(h => h.isDirty);
        const dirtyLogs = logs.filter(l => l.isDirty);

        if (dirtyHabits.length === 0 && dirtyLogs.length === 0) {
            // Optional: Still fetch remote changes if needed, but let's skip if no local changes for now to save bandwidth
            // notifySyncStatus('synced');
            // return;
        }

        const payload = {
            habits: dirtyHabits.map(({ isDirty, ...h }) => h),
            logs: dirtyLogs.map(({ isDirty, ...l }) => ({
                id: l.id,
                habitId: l.habitId,
                date: l.date,
                value: l.value,
                is_completed: l.is_completed,
                updated_at: l.updated_at
            })),
            user_id: localStorage.getItem('df_user_id') || 'default_user'
        };

        try {
            const result = await apiClient.syncData(payload);

            if (result.success) {
                const tx = db.transaction(['habits', 'logs'], 'readwrite');
                if (result.remoteHabits) {
                    for (const h of result.remoteHabits) {
                        await tx.objectStore('habits').put({ ...h, isDirty: false });
                    }
                }
                if (result.remoteLogs) {
                    const logStore = tx.objectStore('logs');
                    const logIndex = logStore.index('by-habit-date');
                    for (const l of result.remoteLogs) {
                        const existing = await logIndex.get([l.habit_id, l.date]);
                        const localLog = {
                            id: existing ? existing.id : undefined, // Preserve local auto-increment ID
                            date: l.date,
                            habitId: l.habit_id,
                            value: l.value,
                            is_completed: l.is_completed === 1,
                            updated_at: l.updated_at,
                            isDirty: false
                        };
                        await logStore.put(localLog);
                    }
                }
                await tx.done;
                notifySyncStatus('synced');
            } else {
                notifySyncStatus('error');
            }
        } catch (error) {
            console.error('Sync failed:', error);
            notifySyncStatus('error');
        }
    },

    async getDailyCompletionRates() {
        const db = await getDB();
        const habits = await db.getAll('habits');
        const logs = await db.getAll('logs');

        const rates = {};
        const habitMap = new Map(habits.map(h => [h.id, h]));
        const logsByDate = (logs && Array.isArray(logs)) ? logs.reduce((acc, log) => {
            if (!acc[log.date]) acc[log.date] = [];
            acc[log.date].push(log);
            return acc;
        }, {}) : {};

        Object.keys(logsByDate).forEach(date => {
            const dayLogs = logsByDate[date];
            let totalProgress = 0;
            const activeHabits = habits.filter(h => h.is_deleted !== 1);
            if (activeHabits.length === 0) return;

            dayLogs.forEach(log => {
                const habit = habitMap.get(log.habitId);
                if (!habit || habit.is_deleted === 1) return;
                if (habit.type === 'binary') {
                    totalProgress += log.is_completed ? 1 : 0;
                } else {
                    const goal = habit.goal || 1;
                    const val = log.value || 0;
                    if (habit.direction === 'decrease') {
                        // For decrease goals, if value <= goal, then 100% complete
                        // Progress calculation: harder to define "0%" but let's say 
                        // if value <= goal, progress = 1. If value > goal, progress = goal/value ?
                        // Or just binary logic for completion rate in summary.
                        totalProgress += val <= goal ? 1 : Math.max(0, goal / val);
                    } else {
                        totalProgress += Math.min(val / goal, 1);
                    }
                }
            });
            rates[date] = totalProgress / activeHabits.length;
        });
        return rates;
    },

    async getLogsByDate(date) {
        const db = await getDB();
        const allLogs = await db.getAll('logs');
        return allLogs.filter(l => l.date === date);
    },

    async markAllAsDirty() {
        const db = await getDB();
        const tx = db.transaction(['habits', 'logs'], 'readwrite');

        const habits = await tx.objectStore('habits').getAll();
        for (const habit of habits) {
            await tx.objectStore('habits').put({ ...habit, isDirty: true });
        }

        const logs = await tx.objectStore('logs').getAll();
        for (const log of logs) {
            await tx.objectStore('logs').put({ ...log, isDirty: true });
        }

        await tx.done;
        await this.syncWithRemote();
    },

    async clearLocalData() {
        try {
            const db = await getDB();
            const tx = db.transaction(['habits', 'logs', 'photos'], 'readwrite');
            await Promise.all([
                tx.objectStore('habits').clear(),
                tx.objectStore('logs').clear(),
                tx.objectStore('photos').clear()
            ]);
            await tx.done;
        } catch (e) {
            console.error('Failed to clear local data:', e);
        }
    },

    async resetAllData() {
        const userId = localStorage.getItem('df_user_id');
        if (!userId) {
            await this.clearLocalData();
            return;
        }

        try {
            // Call remote reset
            const API_BASE = import.meta.env.VITE_API_ENDPOINT.replace('/api/sync', '');
            const response = await fetch(`${API_BASE}/api/reset`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('df_auth_token')}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                await this.clearLocalData();
            } else {
                throw new Error('Remote reset failed');
            }
        } catch (error) {
            console.error('Reset failed:', error);
            // Even if remote fails, we might want to clear local if user is persistent?
            // For now, let's just clear local if it's already an anonymous-only user
            if (!userId.includes('_')) {
                await this.clearLocalData();
            } else {
                throw error;
            }
        }
    }
};
